import React, { useMemo, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles, Divider, Button } from '@material-ui/core';

import { getCurrencySymbol, toLocaleString } from '../../collums-components/helpers/index';
import { DEFAULT_PHOTO } from '../../collums-constants';
import { DurationText, DurationContainer, ClockIcon } from './common/StyledText';

import { SERVICE, STAFF, TIME, CONFIRM } from '../../constants/steps';
import {
    changeStep,
    updateAppointments,
    changeService,
    clearLocation,
    setCurrentDate,
    setCurrentTimeStep,
    chooseTime,
    setCurrentCategory,
    availableStaff
} from '../../redux/actions/book-appointment';
import {
    getCurrentTime,
    getCurrentLocation,
    getCurrentStaff,
    getCurrentDate,
    getCurrentAppointments,
    getAppointmentIdToRemove,
    getCurrentConfirmStep,
    getCurrentStep,
    getCourseContext,
    getCurrentGiftVouchers
} from '../../redux/selectors/book-appointment';
import { stepStyles } from './styles';
import { getAddress } from './../../utils/helpers/index';
import { getLocationItem } from '../../collums-constants/utils';
import { useHistory } from 'react-router-dom';
import RemoveAppointmentDialog from './RemoveAppointmentDialog';
import AppointmentApi from '../../api/AppointmentApi';
import { CREDIT_CARD } from '../../constants/steps';

export default function BookingAppointmentDetails(clinics) {
    const classes = makeStyles(theme => stepStyles(theme))();

    const dispatch = useDispatch();
    const history = useHistory();

    const currentLocation = useSelector(getCurrentLocation);
    const currentStaff = useSelector(getCurrentStaff);
    const currentTime = useSelector(getCurrentTime);
    const currentDate = useSelector(getCurrentDate);
    const currentAppointments = useSelector(getCurrentAppointments);
    const currentGiftVouchers = useSelector(getCurrentGiftVouchers);
    const appointmentIdToRemove = useSelector(getAppointmentIdToRemove);
    const currentStep = useSelector(getCurrentStep);
    const courseContext = useSelector(getCourseContext);
    const isReschedule = useSelector(state => state.bookAppointment.isReschedule);
    const isLinked = useSelector(state => state.bookAppointment.isLinked);
    const hasLinked = useSelector(state => state.bookAppointment.linkedAppointments);
    const renderCards = useSelector(getCurrentConfirmStep);
    const [showRemoveServiceDialog, setShowRemoveServiceDialog] = useState(false);
    const [serviceIndexToRemove, setServiceIndexToRemove] = useState(null);
    const [isRemovingCourse, setIsRemovingCourse] = useState(false);
    const [cachedPrices, setCachedPrices] = useState({});
    const [isChangingService, setIsChangingService] = useState(false);
    const availableStaffList = useSelector(state => state.bookAppointment.availableStaff);

    const getPractitionerAvatar = practitioner => {
        if (!practitioner) return null;

        const defaultAvatar =
            practitioner.gender === 'Female' ? DEFAULT_PHOTO.DEFAULT_FEMALE : DEFAULT_PHOTO.DEFAULT_MALE;

        if (practitioner.avatar) {
            if (practitioner.avatar.startsWith('http')) {
                return practitioner.avatar;
            }
            return (
                'https://collums.imgix.net/' + practitioner.avatar.split('/').pop() + '?w=30&h=30&mask=ellipse&fit=crop'
            );
        }

        return defaultAvatar;
    };

    useEffect(() => {
        if (currentAppointments?.length) {
            const newPrices = {};
            let hasNewPrices = false;

            currentAppointments.forEach(appt => {
                const serviceId = appt.service?.id;
                if (serviceId && cachedPrices[serviceId] === undefined) {
                    let price = null;

                    if (appt.staff?.grossPrice) {
                        price = appt.staff.grossPrice;
                    } else if (appt.service?.salePrice !== undefined) {
                        price = appt.service.salePrice;
                    } else if (appt.service?.defaultPrice !== undefined) {
                        price = appt.service.defaultPrice;
                    } else if (appt.service?.price !== undefined) {
                        price = appt.service.price;
                    }

                    if (price !== null) {
                        newPrices[serviceId] = price;
                        hasNewPrices = true;
                    }
                }
            });

            if (hasNewPrices) {
                setCachedPrices(prev => ({ ...prev, ...newPrices }));
            }
        }
    }, [currentAppointments, cachedPrices]);

    const renderPrice = current => {
        const serviceId = current.service?.id || current.service?._id;

        if (current.service?.defaultPrice === Infinity) {
            return null;
        }

        const isCourseServiceId =
            courseContext?.courseServices && serviceId && courseContext.courseServices.includes(serviceId);
        const isRegularServiceBooking = current.service?._isRegularServiceBooking;
        const isCourseService = isCourseServiceId && isRegularServiceBooking !== true;

        if (isCourseService) {
            return toLocaleString(0);
        }

        if (current.staff?.grossPrice) {
            const price = current.staff.grossPrice;
            return toLocaleString(price);
        }

        // When no staff selected, use min price from available practitioners
        if (!current.staff && availableStaffList?.length > 0) {
            const minPrice = Math.min(...availableStaffList.map(s => s.grossPrice));
            if (isFinite(minPrice)) {
                return toLocaleString(minPrice);
            }
        }

        let price =
            current.service?.salePrice !== undefined
                ? current.service.salePrice
                : current.service?.defaultPrice !== undefined
                ? current.service.defaultPrice
                : current.service?.price !== undefined
                ? current.service.price
                : null;

        // For regular service bookings of course services, use cached price if available
        if (price === null && serviceId && cachedPrices[serviceId] !== undefined) {
            price = cachedPrices[serviceId];
        }

        if (price === null) {
            price = 0;
        }

        return toLocaleString(price);
    };

    const goTo = step => {
        dispatch(changeStep(step));
    };

    const deleteAppointment = async () => {
        if (appointmentIdToRemove && !isReschedule) {
            try {
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Delete appointment timeout')), 10000)
                );

                const deletePromise = AppointmentApi.deletePendingAppointments(appointmentIdToRemove);

                await Promise.race([deletePromise, timeoutPromise]);
            } catch (error) {
                console.error('Error deleting appointment:', error);
            }
        }
    };

    const handleChangeTime = async () => {
        if (isChangingService) return;

        setIsChangingService(true);
        try {
            await deleteAppointment();
            dispatch(setCurrentTimeStep(null));
            dispatch(setCurrentDate(null));
            dispatch(chooseTime(null));
            goTo(TIME);
        } catch (error) {
            console.error('Error during time change:', error);
            dispatch(setCurrentTimeStep(null));
            dispatch(setCurrentDate(null));
            dispatch(chooseTime(null));
            goTo(TIME);
        } finally {
            setIsChangingService(false);
        }
    };

    const handleChangeService = async index => {
        if (isChangingService) return;

        setIsChangingService(true);
        try {
            const tempCurrAppts = [...currentAppointments];
            tempCurrAppts.splice(index, 1);

            await handleChangeTime();

            dispatch(updateAppointments(tempCurrAppts));
            dispatch(setCurrentCategory(null));
            dispatch(changeService(null));
            dispatch(availableStaff([]));

            const navigationUrl =
                window.location.pathname
                    .split('/')
                    .slice(0, 3)
                    .join('/') +
                '?service=' +
                tempCurrAppts.length;

            history.push(navigationUrl);
            goTo(SERVICE);
        } catch (error) {
            console.error('Error during service change:', error);
            const tempCurrAppts = [...currentAppointments];
            tempCurrAppts.splice(index, 1);

            dispatch(updateAppointments(tempCurrAppts));
            dispatch(setCurrentCategory(null));
            dispatch(changeService(null));
            dispatch(availableStaff([]));

            try {
                const navigationUrl =
                    window.location.pathname
                        .split('/')
                        .slice(0, 3)
                        .join('/') +
                    '?service=' +
                    tempCurrAppts.length;

                history.push(navigationUrl);
                goTo(SERVICE);
            } catch (navError) {
                console.error('Navigation fallback error:', navError);
                window.location.reload();
            }
        } finally {
            setIsChangingService(false);
        }
    };

    const handleDeleteService = () => {
        const tempCurrAppts = currentAppointments;
        const removedAppointment = tempCurrAppts[serviceIndexToRemove];
        const isRemovingCourse = removedAppointment?.service?.salePrice !== undefined;

        tempCurrAppts.splice(serviceIndexToRemove, 1);
        dispatch(updateAppointments(tempCurrAppts));
        setServiceIndexToRemove(null);
        setShowRemoveServiceDialog(false);

        if (tempCurrAppts.length === 0) {
            dispatch(setCurrentCategory(null));
            dispatch(changeService(null));
            history.push(
                window.location.pathname
                    .split('/')
                    .slice(0, 3)
                    .join('/') +
                    '?service=' +
                    tempCurrAppts.length
            );
            goTo(SERVICE);
        } else {
            if (isRemovingCourse) {
                const remainingCourses = tempCurrAppts.filter(appt => appt?.service?.salePrice !== undefined);
                if (remainingCourses.length === 0) {
                    dispatch(setCurrentCategory(null));
                    dispatch(changeService(null));
                    const baseUrl =
                        window.location.pathname
                            .split('/')
                            .slice(0, 3)
                            .join('/') +
                        '?service=' +
                        tempCurrAppts.length;

                    const remainingServices = tempCurrAppts.filter(appt => appt?.service?.salePrice === undefined);
                    const urlToNavigate = remainingServices.length > 0 ? baseUrl : baseUrl;

                    history.push(urlToNavigate);
                    goTo(SERVICE);
                    return;
                }

                if (currentStep === SERVICE) {
                    dispatch(setCurrentCategory(null));
                    history.push(
                        window.location.pathname
                            .split('/')
                            .slice(0, 3)
                            .join('/') +
                            '?service=' +
                            tempCurrAppts.length +
                            '&view=courses'
                    );
                    return;
                }

                const lastAppt = tempCurrAppts[tempCurrAppts.length - 1];
                history.push(
                    window.location.pathname
                        .split('/')
                        .slice(0, 3)
                        .join('/') +
                        `/${lastAppt.service.category.id}/${lastAppt.service.id}` +
                        '?service=' +
                        (tempCurrAppts.length - 1) +
                        '&view=courses'
                );
                goTo(TIME);
                return;
            }

            const lastAppt = tempCurrAppts[tempCurrAppts.length - 1];
            history.push(
                window.location.pathname
                    .split('/')
                    .slice(0, 3)
                    .join('/') +
                    `/${lastAppt.service.category.id}/${lastAppt.service.id}` +
                    '?service=' +
                    (tempCurrAppts.length - 1)
            );
            goTo(TIME);
        }
    };

    const setServiceToRemove = index => {
        setServiceIndexToRemove(index);
        const serviceToRemove = currentAppointments[index];
        setIsRemovingCourse(serviceToRemove?.service?.salePrice !== undefined);
        setShowRemoveServiceDialog(true);
    };

    const handleDeletePractitioner = async index => {
        if (isChangingService) return;

        setIsChangingService(true);
        try {
            const tempCurrAppts = [...currentAppointments];
            dispatch(changeService(tempCurrAppts[index].service));
            tempCurrAppts.splice(index, 1, {
                ...tempCurrAppts[index],
                staff: null
            });

            await handleChangeTime();
            await deleteAppointment();

            dispatch(updateAppointments(tempCurrAppts));

            const navigationUrl =
                window.location.pathname
                    .split('/')
                    .slice(0, 3)
                    .join('/') +
                '/' +
                tempCurrAppts[index].service.category.id +
                '/' +
                tempCurrAppts[index].service.id +
                '/?service=' +
                index;

            history.push(navigationUrl);
            goTo(STAFF);
        } catch (error) {
            console.error('Error during practitioner deletion:', error);
            const tempCurrAppts = [...currentAppointments];
            dispatch(changeService(tempCurrAppts[index].service));
            tempCurrAppts.splice(index, 1, {
                ...tempCurrAppts[index],
                staff: null
            });

            dispatch(updateAppointments(tempCurrAppts));
            dispatch(setCurrentTimeStep(null));
            dispatch(setCurrentDate(null));
            dispatch(chooseTime(null));

            try {
                const navigationUrl =
                    window.location.pathname
                        .split('/')
                        .slice(0, 3)
                        .join('/') +
                    '/' +
                    tempCurrAppts[index].service.category.id +
                    '/' +
                    tempCurrAppts[index].service.id +
                    '/?service=' +
                    index;

                history.push(navigationUrl);
                goTo(STAFF);
            } catch (navError) {
                console.error('Navigation fallback error:', navError);
                window.location.reload();
            }
        } finally {
            setIsChangingService(false);
        }
    };

    const formatDate = value => {
        return `${format(value, 'HH:mma')}`;
    };

    const canShowTotal = useMemo(() => {
        if (currentAppointments?.length) {
            const hasShowPriceOnline = currentAppointments.some(appt => appt.service?.showPriceOnline);
            const hasSalePrice = currentAppointments.some(appt => appt.service?.salePrice !== undefined);
            const hasDefaultPrice = currentAppointments.some(appt => appt.service?.defaultPrice !== undefined);
            const hasPrice = currentAppointments.some(appt => appt.service?.price !== undefined);
            const hasStaffPrice = currentAppointments.some(appt => appt.staff?.grossPrice !== undefined);

            const isServiceFlow = !courseContext && currentAppointments.length > 0;

            const result =
                courseContext ||
                hasShowPriceOnline ||
                hasSalePrice ||
                hasDefaultPrice ||
                hasPrice ||
                hasStaffPrice ||
                isServiceFlow;
            return result;
        }
        return !!currentGiftVouchers.length;
    }, [currentAppointments, courseContext, currentGiftVouchers]);

    const showTotal = () => {
        const total = currentAppointments.reduce((acc, curr) => {
            let price = 0;
            const serviceId = curr.service?.id || curr.service?._id;

            const isCourseServiceId =
                courseContext?.courseServices && serviceId && courseContext.courseServices.includes(serviceId);
            const isRegularServiceBooking = curr.service?._isRegularServiceBooking;
            // Show zero price only if it's a course service AND explicitly NOT marked as regular booking
            const shouldShowZeroPrice = isCourseServiceId && isRegularServiceBooking !== true;

            if (shouldShowZeroPrice) {
                price = 0;
            } else if (curr.staff?.grossPrice) {
                price = curr.staff.grossPrice;
            } else if (!curr.staff && availableStaffList?.length > 0) {
                const minPrice = Math.min(...availableStaffList.map(s => s.grossPrice));
                price = isFinite(minPrice) ? minPrice : 0;
            } else {
                price =
                    curr.service?.defaultPrice !== undefined
                        ? curr.service.defaultPrice
                        : curr.service?.price !== undefined
                        ? curr.service.price
                        : null;

                if (price === null && serviceId && cachedPrices[serviceId] !== undefined) {
                    price = cachedPrices[serviceId];
                } else if (price === null) {
                    price = 0;
                }
            }

            return acc + price;
        }, 0);

        if (isNaN(total)) {
            return '';
        }

        if (currentGiftVouchers.length) {
            return getCurrencySymbol() + parseFloat(currentGiftVouchers[0].value).toFixed(2);
        }

        const result = (!currentStaff ? 'From: ' : '') + getCurrencySymbol() + parseFloat(total).toFixed(2);
        return result;
    };

    const printDuration = loc => {
        if (!loc || !loc.staffs || !Array.isArray(loc.staffs)) {
            const defaultDuration = loc?.defaultDuration;
            if (defaultDuration === Infinity || !isFinite(defaultDuration)) {
                return null;
            }
            return defaultDuration || 'N/A';
        }

        const durations = loc.staffs.map(staff => {
            if (staff.duration) {
                return staff.duration;
            }
            return loc?.defaultDuration;
        });

        const validDurations = durations.filter(
            duration => duration !== Infinity && isFinite(duration) && duration != null
        );

        if (validDurations.length === 0) {
            return null;
        }

        const minDuration = Math.min(...validDurations);
        const maxDuration = Math.max(...validDurations);
        return (minDuration !== maxDuration ? 'From ' : '') + minDuration;
    };

    return (
        <>
            {currentLocation && (
                <div className={classes.detailContainer}>
                    {currentLocation.accountName && (
                        <>
                            <div className={`${classes.detailItem} ${classes.locationSection}`}>
                                <b className={`${classes.locationAccountName} ${classes.locationAccountNameSpacing}`}>
                                    {currentLocation.accountName}
                                </b>
                                <div className={classes.row}>
                                    <span className={classes.locationAddress}>{getAddress(currentLocation)}</span>
                                    {!(isLinked || (hasLinked && hasLinked.length)) &&
                                    clinics.length > 1 &&
                                    currentAppointments.length === 1 /* eslint-disable-next-line */ && (
                                            <a
                                                href="#"
                                                onClick={async () => {
                                                    await deleteAppointment();
                                                    dispatch(clearLocation());
                                                }}
                                            >
                                                change
                                            </a>
                                        )}
                                </div>
                            </div>
                            <Divider />
                        </>
                    )}
                    {currentAppointments.map(
                        (current, index) =>
                            current.service && (
                                <React.Fragment key={index}>
                                    <div className={`${classes.detailItem} ${classes.serviceSection}`}>
                                        <div className={`${classes.row} ${classes.serviceNameSpacing}`}>
                                            <b className={classes.interText700}>
                                                {getLocationItem(current.service, currentLocation.id)?.onlineName ||
                                                    current.service.name}
                                            </b>
                                            {(current.service.showPriceOnline ||
                                                current.service.salePrice !== undefined ||
                                                courseContext ||
                                                current.service?.defaultPrice !== undefined ||
                                                current.service?.price !== undefined ||
                                                current.staff?.grossPrice !== undefined ||
                                                current.service) && (
                                                <b className={classes.interText700}>
                                                    {!current.staff &&
                                                        current.service.salePrice === undefined &&
                                                        !courseContext &&
                                                        'From: '}
                                                    {renderPrice(current)}
                                                </b>
                                            )}
                                        </div>
                                        <div className={`${classes.row} ${classes.durationSpacing}`}>
                                            {current.service.showDurationOnline &&
                                                !current.service.salePrice &&
                                                (() => {
                                                    let durationValue;

                                                    if (current.staff && current.staff.duration) {
                                                        durationValue = current.staff.duration;
                                                    } else {
                                                        durationValue = current.service.locations
                                                            ? printDuration(
                                                                  getLocationItem(current.service, currentLocation.id)
                                                              )
                                                            : printDuration(current.service.location);
                                                    }

                                                    if (
                                                        durationValue === Infinity ||
                                                        durationValue === null ||
                                                        !isFinite(durationValue)
                                                    ) {
                                                        return null;
                                                    }

                                                    return (
                                                        <DurationContainer>
                                                            <ClockIcon />
                                                            <DurationText>{durationValue} mins</DurationText>
                                                        </DurationContainer>
                                                    );
                                                })()}
                                            {!current.service.showDurationOnline && <span>&nbsp;</span>}
                                            <div>
                                                {!isReschedule && (
                                                    <>
                                                        {/* eslint-disable-next-line */}
                                                        <a
                                                            onClick={() => setServiceToRemove(index)}
                                                            style={{ color: 'red' }}
                                                        >
                                                            remove
                                                        </a>
                                                        <span>&nbsp;&nbsp;</span>
                                                    </>
                                                )}
                                                {!(
                                                    isLinked ||
                                                    (hasLinked && hasLinked.length) ||
                                                    current.service?.salePrice !== undefined
                                                ) /* eslint-disable-next-line */ && (
                                                    <a
                                                        onClick={() => handleChangeService(index)}
                                                        style={{
                                                            opacity: isChangingService ? 0.5 : 1,
                                                            pointerEvents: isChangingService ? 'none' : 'auto'
                                                        }}
                                                    >
                                                        {isChangingService ? 'changing...' : 'change'}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`${classes.detailItem} ${classes.practitionerSpacing}`}>
                                        <div className={classes.practitionerRow}>
                                            <div className={classes.practitionerInfo}>
                                                {current.staff ? (
                                                    <img
                                                        src={getPractitionerAvatar(current.staff)}
                                                        alt={`${current.staff.displayName} avatar`}
                                                        className={classes.practitionerAvatar}
                                                    />
                                                ) : (
                                                    <img
                                                        src="/icons/any-practitioner.svg"
                                                        alt="Any practitioner icon"
                                                        className={classes.anyPractitionerIcon}
                                                    />
                                                )}
                                                <b>{current.staff ? current.staff.displayName : 'any practitioner'}</b>
                                            </div>
                                            {!(
                                                isLinked ||
                                                (hasLinked && hasLinked.length) ||
                                                current.service?.salePrice !== undefined
                                            ) /* eslint-disable-next-line */ && (
                                                <a
                                                    href="#"
                                                    onClick={() => handleDeletePractitioner(index)}
                                                    style={{
                                                        opacity: isChangingService ? 0.5 : 1,
                                                        pointerEvents: isChangingService ? 'none' : 'auto'
                                                    }}
                                                >
                                                    {isChangingService ? 'changing...' : 'change'}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    {index === currentAppointments.length - 1 && !isReschedule && (
                                        <div className={classes.addAnotherServiceSpacing}>
                                            <button
                                                style={{
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                    marginLeft: 16,
                                                    background: 'none',
                                                    border: 'none',
                                                    textDecoration: 'none',
                                                    font: 'inherit',
                                                    padding: 0,
                                                    color: '#3083EC',
                                                    size: '16px !important',
                                                    fontFamily: 'Inter, sans-serif'
                                                }}
                                                onClick={async () => {
                                                    await deleteAppointment();
                                                    dispatch(changeStep(SERVICE));
                                                    dispatch(setCurrentCategory(null));
                                                    dispatch(changeService(null));

                                                    const tempCurrAppts = [...currentAppointments];
                                                    if (tempCurrAppts.length > 0) {
                                                        tempCurrAppts.pop();
                                                    }
                                                    dispatch(updateAppointments(tempCurrAppts));

                                                    const baseUrl =
                                                        window.location.pathname
                                                            .split('/')
                                                            .slice(0, 3)
                                                            .join('/') +
                                                        '?service=' +
                                                        tempCurrAppts.length +
                                                        '&view=services';

                                                    history.push(baseUrl);
                                                }}
                                            >
                                                Add another service
                                            </button>
                                        </div>
                                    )}
                                    <Divider />
                                </React.Fragment>
                            )
                    )}
                    {currentGiftVouchers.map((current, index) => (
                        <React.Fragment key={index}>
                            <div className={`${classes.detailItem} ${classes.serviceSection}`}>
                                <div className={`${classes.row} ${classes.serviceNameSpacing}`}>
                                    <b className={classes.interText700}>Gift Voucher {toLocaleString(current.value)}</b>
                                </div>
                                <div className={`${classes.row} ${classes.durationSpacing}`}>
                                    <div>
                                        {/* eslint-disable-next-line */}
                                        {/*<a onClick={() => setServiceToRemove(index)} style={{ color: 'red' }}>*/}
                                        {/*    remove*/}
                                        {/*</a>*/}
                                        <span>&nbsp;&nbsp;</span>
                                    </div>
                                </div>
                            </div>

                            <Divider />
                        </React.Fragment>
                    ))}
                    {currentDate && (
                        <div className={`${classes.detailItem} ${classes.appointmentDateSpacing}`}>
                            <div className={classes.row}>
                                <b>{format(currentDate, 'EEEE do MMMM yyyy')}</b>
                                {/* eslint-disable-next-line */}
                                <a
                                    href="#"
                                    onClick={() => handleChangeTime()}
                                    style={{
                                        opacity: isChangingService ? 0.5 : 1,
                                        pointerEvents: isChangingService ? 'none' : 'auto'
                                    }}
                                >
                                    {isChangingService ? 'changing...' : 'change'}
                                </a>
                            </div>

                            {currentTime && (
                                <>
                                    <span>{formatDate(currentTime)}</span>
                                    <Divider />
                                </>
                            )}
                        </div>
                    )}
                    {currentAppointments && (
                        <div className={`${classes.detailItem} ${classes.totalSection}`}>
                            <div className={classes.row}>
                                <b className={classes.interText700} style={{ fontSize: '18px' }}>
                                    Total
                                </b>
                                {canShowTotal && <b className={classes.totalText}>{showTotal()}</b>}
                            </div>
                        </div>
                    )}
                    {currentAppointments &&
                        currentAppointments.some(appt => appt.service?.salePrice !== undefined) &&
                        renderCards !== CREDIT_CARD &&
                        !!renderCards && (
                            <div className={classes.detailItem} style={{ marginTop: '1rem' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    className={classes.buyNowButton}
                                    onClick={() => {
                                        dispatch(changeStep(CONFIRM));
                                    }}
                                >
                                    Buy now
                                </Button>
                            </div>
                        )}
                </div>
            )}

            <RemoveAppointmentDialog
                open={showRemoveServiceDialog}
                onConfirm={handleDeleteService}
                onCancel={() => setShowRemoveServiceDialog(false)}
                isCourse={isRemovingCourse}
            />
        </>
    );
}
